import React, { PropsWithChildren } from 'react';
import { AppState, AppThunkContext } from '@fullstory/redux';
import { Flags } from '@fullstory/client-api';
import { Store } from 'redux';
import { DeepPartial } from '@fullstory/typeutils';
import { Provider } from 'react-redux';
import { FlagsContext, NotificationsContext, XhrContext } from '@fullstory/react-hooks';
import { Router } from 'react-router-dom';
import { createMemoryHistory, History } from 'history';
import { NotificationsTray } from '@fullstory/tomato-components';
import { createTestReduxStore, xhr as sharedXhr } from '../../utils/redux';
import { NetworkController } from '@fullstory/util/xhr';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

interface WrapperOptions {
  flags?: Flags;
  history?: History;
  initialState?: DeepPartial<AppState>;
  thunkContext?: DeepPartial<Omit<AppThunkContext, 'flags'>>;
  store?: Store | null;
  xhr?: NetworkController | null;
}

export const StoreWrapper: React.FC<PropsWithChildren<WrapperOptions>> = ({
  flags = new Flags(),
  history = createMemoryHistory(),
  initialState = {},
  thunkContext = {},
  children,
  store,
  xhr,
}) => {
  const notifications = new NotificationsTray();

  const configuredStore =
    store ||
    createTestReduxStore({
      flags,
      initialState,
      thunkContext,
      networkController: xhr,
    });
  const configuredXhr = xhr || sharedXhr;

  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        // turns off retries by default only
        // (can be overriden by individual queries)
        retry: false,
      },
    },
  });

  return (
    <NotificationsContext.Provider value={notifications}>
      <XhrContext.Provider value={configuredXhr}>
        <QueryClientProvider client={queryClient}>
          <Provider store={configuredStore}>
            <Router history={history}>
              <FlagsContext.Provider value={flags}>{children}</FlagsContext.Provider>
            </Router>
          </Provider>
        </QueryClientProvider>
      </XhrContext.Provider>
    </NotificationsContext.Provider>
  );
};
